import React, { useEffect, useState } from "react";
import Modal from "react-modal";
import { RoomRegisterModalProps } from "./types";
import {
  ButtonSection,
  CancelContainer,
  Container,
  ContentContainer,
  RowFormContainer,
  TitleContainer,
  TopSection,
} from "./RoomRegisterModal.styles";
import UI from "@mile-meeting/mile-ui";
import {
  RoomBuildingField,
  RoomCapacityField,
  RoomDetailInfoField,
  RoomFloorField,
  RoomImageUpload,
  RoomNameField,
  TabletUsageField,
} from "../component";
import { TBuilding } from "types/building.types";
import { useToast } from "hook/useToast";
import { uploadImageToS3 } from "apis/uploadImage";
import { addMeetingRoom } from "apis/meetings/meeting-room";
import AlertModal from "uis/modal/alert-modal";
import UsageTabletSettingModal from "../UsageTabletSettingModal/UsageTabletSettingModal";
import { is } from "date-fns/locale";

const modalStyle = {
  content: {
    top: "50%",
    left: "50%",
    right: "auto",
    bottom: "auto",
    marginRight: "-50%",
    maxHeight: `${window.innerHeight - 100}px`,
    width: "600px",
    borderRadius: "16px",
    transform: "translate(-50%, -50%)",
    padding: "0",
  },
  overlay: {
    backgroundColor: "var(--component-color-material-dimmer)",
    zIndex: 1000,
  },
};

Modal.setAppElement("#root");

const RoomRegisterModal: React.FC<RoomRegisterModalProps> = ({ modalIsOpen, closeModal, onUpdate }) => {
  const [image, setImage] = useState<File | string | undefined>();
  const [name, setName] = useState<string>();
  const [capacity, setCapacity] = useState<number>();
  const [buildingName, setBuildingName] = useState<string | undefined>();
  const [buildingId, setBuildingId] = useState<string | undefined>();
  const [floorId, setFloorId] = useState<string>();
  const [floorName, setFloorName] = useState<string>();
  const [detailInfo, setDetailInfo] = useState<string>("");
  const [buildingList, setBuildingList] = useState<(TBuilding | null)[]>([]);
  const [showTableUsedAlert, setShowTableUsedAlert] = useState<boolean>(false);
  const [showUsageTabletSettingModal, setShowUsageTabletSettingModal] = useState<boolean>(false);
  const [isTabletExternalUsage, setIsTabletExternalUsage] = useState<boolean>(false);
  const [isTabletInternalUsage, setIsTabletInternalUsage] = useState<boolean>(false);
  const { showToast } = useToast();

  const enableAddRoomButton = name && capacity && capacity <= 1000 && capacity >= 1;

  //* INFO: 건물 정보 변경 시 층 정보 초기화
  const onChangeBuildingInfo = (buildingName?: string, buildingId?: string) => {
    setBuildingName(buildingName);
    setBuildingId(buildingId);
    setFloorId(undefined);
    setFloorName(undefined);
  };

  //* INFO: 이미지 업로드
  const uploadImage = async () => {
    if (!image) {
      return null;
    }
    if (typeof image === "string") return image;
    const formData = new FormData();
    formData.append("file", image as Blob);
    const imageUrl = await uploadImageToS3(formData);
    return imageUrl;
  };

  //* INFO: 마일 회의실 등록
  const handleRegisterRoom = async () => {
    if (!enableAddRoomButton) return;
    const imageUrl = await uploadImage();
    const building = buildingList.find((item) => item?.name === buildingName);
    const floorId = building?.floors.find((item) => item?.name === floorName)?.id;

    const res = await addMeetingRoom(
      name,
      capacity ?? 0,
      isTabletInternalUsage,
      isTabletExternalUsage,
      buildingId ?? "",
      floorId ?? "",
      detailInfo,
      imageUrl ?? undefined
    );

    if (res.success) {
      onUpdate?.();
      closeModal();
      showToast("회의실이 추가되었습니다.", "success");
    } else {
      showToast(res.message ?? "회의실을 추가할 수 없습니다.", "error");
    }
  };

  useEffect(() => {
    if (!modalIsOpen) {
      //TODO: 모달이 열려도 왜 초기화 안되는지 확인 필요
      setImage(undefined);
      setName("");
      setCapacity(0);
      setBuildingName("");
      setBuildingId("");
      setFloorId("");
      setFloorName("");
      setDetailInfo("");
      setIsTabletExternalUsage(false);
      setIsTabletInternalUsage(false);
    }
  }, [modalIsOpen]);

  return (
    <Modal isOpen={modalIsOpen} onRequestClose={closeModal} style={modalStyle}>
      <Container>
        <CancelContainer>
          <UI.IconButton.Normal
            type="primary"
            icon="x"
            size={20}
            color="var(--alias-color-label-normal)"
            onClick={closeModal}
          />
        </CancelContainer>
        <TitleContainer>
          <UI.Typography type="title03" fontStyle="bold" color="var(--alias-color-label-normal)">
            회의실 등록
          </UI.Typography>
        </TitleContainer>
        <ContentContainer>
          <TopSection>
            <RoomImageUpload image={image} onChange={setImage} />
            <RoomNameField name={name ?? ""} onChange={setName} />
            <RoomCapacityField capacity={capacity ?? 0} onChange={setCapacity} />
            <RowFormContainer>
              <RoomBuildingField
                onChange={onChangeBuildingInfo}
                selectedBuilding={buildingName}
                selectedBuildingId={buildingId}
                buildingList={buildingList}
                onChangeBuildingList={setBuildingList}
              />
              <RoomFloorField
                buildingName={buildingName}
                buildingId={buildingId}
                floorId={floorId}
                floorName={floorName}
                onChange={(id, name) => {
                  setFloorName(name);
                  setFloorId(id);
                }}
                onUpdate={onUpdate}
                buildingList={buildingList}
                onChangeBuildingList={setBuildingList}
              />
            </RowFormContainer>
            <TabletUsageField
              isTabletExternalUsage={isTabletExternalUsage}
              isTabletInternalUsage={isTabletInternalUsage}
              onClickToggle={() => {
                setShowUsageTabletSettingModal(true);
              }}
            />
            <RoomDetailInfoField detailInfo={detailInfo} onChange={setDetailInfo} />
          </TopSection>
          <ButtonSection>
            <UI.Button
              title="등록하기"
              size="large"
              primary
              disabled={!enableAddRoomButton}
              onClick={handleRegisterRoom}
            />
          </ButtonSection>
        </ContentContainer>
      </Container>
      <UsageTabletSettingModal
        modalIsOpen={showUsageTabletSettingModal}
        usageInternal={isTabletInternalUsage}
        usageExternal={isTabletExternalUsage}
        closeModal={() => {
          setShowUsageTabletSettingModal(false);
        }}
        onSave={(isInternal, isExternal) => {
          setIsTabletInternalUsage(isInternal);
          setIsTabletExternalUsage(isExternal);
          setShowUsageTabletSettingModal(false);
        }}
      />
      <AlertModal
        modalIsOpen={showTableUsedAlert}
        closeModal={() => setShowTableUsedAlert(false)}
        onChange={() => {
          window.open("https://mile.channel.io/home");
        }}
        content={"사용을 원하시면 문의 부탁드립니다."}
        btnTitle="태블릿 사용 플랜 문의하기"
        title="이용 중이신 플랜에서는 태블릿 사용이 불가합니다."
      />
    </Modal>
  );
};

export default React.memo(RoomRegisterModal);
